import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { Project, ProjectPriority, ProjectStatus } from './types';

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** Departments validated by the backend (DEPTS_VALID). */
export const DEPARTMENT_OPTIONS = [
  { value: 'it', label: 'IT' },
  { value: 'digital_marketing', label: 'Digital Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'hr', label: 'HR' },
];

export const DEPT_LABELS: Record<string, string> = {
  it: 'IT',
  digital_marketing: 'Digital Mktg',
  sales: 'Sales',
  hr: 'HR',
  management: 'Management',
};

export const STATUS_TONE: Record<ProjectStatus, BadgeTone> = {
  active: 'success',
  on_hold: 'warning',
  completed: 'info',
  cancelled: 'danger',
};

export const STATUS_LABEL: Record<ProjectStatus, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const PRIORITY_TONE: Record<ProjectPriority, BadgeTone> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

/** Progress percentage — mirrors the backend-derived counts exactly. */
export function progressPct(project: Pick<Project, 'task_count' | 'done_count'>): number {
  const total = project.task_count ?? 0;
  const done = project.done_count ?? 0;
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

export interface DeadlineInfo {
  label: string;
  urgent: boolean;
}

/** Deadline badge text — matches legacy: Overdue / Today / Nd left. */
export function deadlineInfo(deadline?: string | null): DeadlineInfo | null {
  if (!deadline) return null;
  const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000);
  if (Number.isNaN(days)) return null;
  let label: string;
  if (days < 0) label = 'Overdue';
  else if (days === 0) label = 'Today';
  else label = `${days}d left`;
  return { label, urgent: days < 3 };
}
